'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
/* Per-icon entry points, not the package barrel: the barrel's index.d.ts
   references every icon's declaration (3,000+ files), which alone pushed the
   cold typecheck past the heap Vercel's build machines allow. `types` has
   declarations but no runtime module, so it stays an `import type`. */
import type { Icon as PhosphorIcon } from '@phosphor-icons/react/dist/lib/types';
import { BedIcon } from '@phosphor-icons/react/dist/csr/Bed';
import { BookOpenIcon } from '@phosphor-icons/react/dist/csr/BookOpen';
import { DropIcon } from '@phosphor-icons/react/dist/csr/Drop';
import { FlameIcon } from '@phosphor-icons/react/dist/csr/Flame';
import { HeartbeatIcon } from '@phosphor-icons/react/dist/csr/Heartbeat';
import { FlowerBloomIcon } from 'components/science/explainers/FlowerBloomIcon';
import { TestTubeIcon } from '@phosphor-icons/react/dist/csr/TestTube';
import { ThermometerSimpleIcon } from '@phosphor-icons/react/dist/csr/ThermometerSimple';

import MeshFlow from 'components/science/home/MeshFlow';
import TypewriterText from 'components/science/landing-v2/TypewriterText';
import type { QueryBlock } from 'components/science/explainers/search/conversation';
import 'components/science/explainers/tokens.css';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { useGlobalUI } from 'contexts/GlobalUIContext';

import styles from './explainers-client.module.css';

/* Loaded on first open; the panel renders nothing until then, and its 132KB
   corpus arrives from /api/science/explainers-search rather than page props. */
const ExplainersChatPanel = dynamic(
  () => import('components/science/explainers/search/SearchPanel'),
  { ssr: false },
);

/* The explainers hero palette (EXPLAINERS_DITHER_SHADER, softened): an extra
   mid-violet for an even cool→warm sweep, warm end pulled toward the pink
   family so it no longer pops as a separate corner. */
const HERO_COLORS = [
  '#12081E',
  '#321B85',
  '#5B2ED0',
  '#8A5BE0',
  '#F0568F',
  '#E88A5A',
];

/* A distinct Phosphor icon per topic, shown on the cluster rows. Keys are the
   5-category set; the legacy keys are harmless aliases. */
/* Phosphor icons plus the one drawn bloom, which is not one. Both satisfy the
   only contract the row uses: a size, and a weight it may ignore. */
type ClusterIcon = PhosphorIcon | typeof FlowerBloomIcon;

const CLUSTER_ICON: Record<string, ClusterIcon> = {
  sleep: BedIcon,
  movement: FlameIcon,
  heart: HeartbeatIcon,
  metabolic: DropIcon,
  women: FlowerBloomIcon,
  // legacy aliases
  recovery: HeartbeatIcon,
  temperature: ThermometerSimpleIcon,
  bloodvision: TestTubeIcon,
};

export interface ClusterCard {
  key: string;
  name: string;
  blurb: string;
  pillarSlug: string;
  articleCount: number;
}

const ExplainersClient = ({ clusters }: { clusters: ClusterCard[] }) => {
  const t = useTranslations('science');
  useGlobalUI({ headerCollaspable: false });
  const bannerPx = useMarketingBannerOffset();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchBlocks, setSearchBlocks] = useState<QueryBlock[]>([]);
  const [searchMounted, setSearchMounted] = useState(false);
  const openSearch = () => {
    setSearchMounted(true);
    setSearchOpen(true);
  };
  const searchPhrases = [
    t('science.explainers.search.phrase1'),
    t('science.explainers.search.phrase2'),
    t('science.explainers.search.phrase3'),
    t('science.explainers.search.phrase4'),
    t('science.explainers.search.phrase5'),
    t('science.explainers.search.phrase6'),
  ];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <main className={`expScope ${styles.page}`}>
        <div
          className={styles.heroFrame}
          style={{ '--banner-px': `${bannerPx}px` } as React.CSSProperties}
        >
          <header className={styles.hero}>
            <div className={styles.heroShader} aria-hidden="true">
              <MeshFlow colors={HERO_COLORS} />
            </div>
            <div className={styles.heroScrim} aria-hidden="true" />
            <div className={styles.heroInner}>
              <div className={styles.eyebrow}>
                <BookOpenIcon weight="regular" size={14} />
                {t('science.explainers.hero.eyebrow')}
              </div>
              <h1 className={styles.h1}>{t('science.explainers.hero.title')}</h1>
              <p className={styles.lede}>{t('science.explainers.hero.lede')}</p>
              <button
                type="button"
                className={styles.searchPill}
                onClick={() => openSearch()}
                aria-label={t('science.explainers.hero.searchLabel')}
              >
                <svg
                  className={styles.glass}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <span className={styles.phrase} aria-hidden="true">
                  <TypewriterText phrases={searchPhrases} />
                </span>
                <span className={styles.hint} aria-hidden="true">
                  ⌘K
                </span>
              </button>
            </div>
          </header>
        </div>

        <section className={styles.body}>
          <div className={styles.bodyInner}>
            <div className={styles.sectionLabel}>
              {t('science.explainers.browse')}
            </div>
            {/* No expand affordance. There are exactly five clusters and
                ViewAllButton returns null whenever total <= shown, so the
                control could never render. */}
            <div className={styles.list}>
              {clusters.map((c) => {
                const Ico = CLUSTER_ICON[c.key] || BookOpenIcon;
                return (
                  <Link
                    className={styles.row}
                    key={c.key}
                    href={`/science/explainers/category/${c.key}/`}
                    aria-label={c.name}
                  >
                    <span className={styles.rowIcon} aria-hidden="true">
                      <Ico size={22} weight="fill" />
                    </span>
                    <div className={styles.rowText}>
                      <div className={styles.rowHead}>
                        <span className={styles.rowName}>{c.name}</span>
                        <span className={styles.count}>
                          {c.articleCount === 1
                            ? t('science.explainers.guideCountOne')
                            : t('science.explainers.guideCount', {
                                count: c.articleCount,
                              })}
                        </span>
                      </div>
                      <p className={styles.rowBlurb}>{c.blurb}</p>
                    </div>
                    <span className={styles.arrowBadge} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 10 10" fill="none">
                        <path
                          fill="currentColor"
                          d="M7.703 5.8H.398V4.6h7.305l-3.36-3.36.855-.84 4.8 4.8-4.8 4.8-.855-.84 3.36-3.36Z"
                        />
                      </svg>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
        <div className={styles.footerRule} />
      </main>

      {searchMounted ? (
        <ExplainersChatPanel
          open={searchOpen}
          blocks={searchBlocks}
          onBlocks={setSearchBlocks}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ExplainersClient;
